import { createTheme } from '@mui/material/styles'

// Export the getTheme function to create a theme based on mode
export const getTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#3498db', // Your primary light blue
        dark: '#2980b9', // Your hover blue
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#2c3e50', // Your dark blue/navy from header
        contrastText: '#ffffff',
      },
      background: {
        default: mode === 'dark' ? '#121212' : '#f5f5f5',
        paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.87)', // Material UI default
        secondary: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)', // Material UI default
        disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)',
      },
    },
    spacing: (factor) => `${0.5 * factor}rem`, // Base spacing unit of 0.5rem
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      // Material UI style typography matching documentation
      h1: {
        fontSize: '2.125rem', // ~34px - Large prominent heading
        fontWeight: 400,
        lineHeight: 1.235,
        letterSpacing: '0.00735em',
        // Color adapts automatically via theme.palette.text.primary
      },
      h2: {
        fontSize: '1.75rem', // ~28px - Section headings
        fontWeight: 400,
        lineHeight: 1.334,
        letterSpacing: '0em',
        paddingBottom: '0.75rem',
        borderBottom: mode === 'dark' 
          ? '2px solid rgba(52, 152, 219, 0.3)' 
          : '2px solid #3498db',
        marginBottom: '1.25rem',
      },
      h3: {
        fontSize: '1.5rem', // ~24px
        fontWeight: 400,
        lineHeight: 1.334,
        letterSpacing: '0em',
      },
      h4: {
        fontSize: '1.25rem', // ~20px - Section headings like Material UI docs
        fontWeight: 500,
        lineHeight: 1.6,
        letterSpacing: '0.0075em',
      },
      h5: {
        fontSize: '1.125rem', // ~18px
        fontWeight: 400,
        lineHeight: 1.334,
        letterSpacing: '0em',
      },
      h6: {
        fontSize: '1rem', // ~16px - Card titles
        fontWeight: 500,
        lineHeight: 1.6,
        letterSpacing: '0.0075em',
      },
      subtitle1: {
        fontSize: '1rem', // ~16px
        fontWeight: 400,
        lineHeight: 1.75,
        letterSpacing: '0.00938em',
      },
      subtitle2: {
        fontSize: '0.875rem', // ~14px
        fontWeight: 500,
        lineHeight: 1.57,
        letterSpacing: '0.00714em',
      },
      body1: {
        fontSize: '1rem', // ~16px - Standard body text
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
      },
      body2: {
        fontSize: '0.875rem', // ~14px - Card descriptions, secondary text
        fontWeight: 400,
        lineHeight: 1.43,
        letterSpacing: '0.01071em',
      },
      button: {
        fontSize: '0.875rem', // ~14px
        fontWeight: 500,
        lineHeight: 1.75,
        letterSpacing: '0.02857em',
        textTransform: 'none', // Keep as-is per user preference
      },
      caption: {
        fontSize: '0.75rem', // ~12px
        fontWeight: 400,
        lineHeight: 1.66,
        letterSpacing: '0.03333em',
      },
      overline: {
        fontSize: '0.75rem', // ~12px
        fontWeight: 400,
        lineHeight: 2.66,
        letterSpacing: '0.08333em',
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            padding: '1rem',
            marginBottom: '1rem',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#2c3e50',
            color: '#ffffff',
            fontWeight: 600,
            letterSpacing: '0.01em',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(52, 152, 219, 0.2)'
                  : 'rgba(52, 152, 219, 0.1)',
            },
          },
        },
      },
    },
  })

export default getTheme('light') // Default theme
